
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePageParam } from "@/hooks/use-page-param";
import { ListPagination } from "@/components/ui/list-pagination";
import { Pencil, Trash2, ArrowUpAZ, ArrowDownAZ, Filter, Star, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import { SearchInput } from "@/components/ui/search-input";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AgenciaModal } from "@/components/agencias/AgenciaModal";
import { AgenciaEditModal } from "@/components/agencias/AgenciaEditModal";
import { useToast } from "@/hooks/use-toast";
import { useToggleAgenciaFavorita } from "@/hooks/use-agencias";
import { useInternalLink } from "@/hooks/use-internal-link";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { writeExpectingRows } from "@/lib/supabase-write";
import { AGENCIA_PORTES, type Agencia, type AgenciaPorte } from "@/types/agencia";

type OrdenacaoType = "nome_asc" | "nome_desc" | "porte_asc" | "porte_desc";

export default function Agencias() {
  const [searchTerm, setSearchTerm] = useState("");
  const [agenciaParaExcluir, setAgenciaParaExcluir] = useState<{ id: string; nome: string } | null>(null);
  const [porteFiltro, setPorteFiltro] = useState<AgenciaPorte | "todas">("todas");
  const [ordenacao, setOrdenacao] = useState<OrdenacaoType>("nome_asc");
  const [selectedAgencia, setSelectedAgencia] = useState<Agencia | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const toggleFavorita = useToggleAgenciaFavorita();
  const linkProps = useInternalLink();

  // Query para buscar agências
  const { data: agencias = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["agencias"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select()
        .order("nome");

      if (error) {
        console.error("Erro ao buscar agências:", error);
        throw error;
      }

      return (data || []) as Agencia[];
    },
  });

  // Função para excluir agência
  const handleDelete = async (agenciaId: string) => {
    try {
      // Primeiro verifica se a agência existe
      const { data: agenciaExiste } = await supabase
        .from("agencias")
        .select("id")
        .eq("id", agenciaId)
        .single();

      if (!agenciaExiste) {
        console.error("Agência não encontrada");
        toast({
          title: "Erro",
          description: "Agência não encontrada",
          variant: "destructive",
        });
        return;
      }

      // Tenta excluir a agência (a existência já foi checada acima, então 0
      // linhas aqui = bloqueio de RLS, não "não existe" — vira erro explícito).
      await writeExpectingRows(
        supabase.from("agencias").delete().eq("id", agenciaId),
        "excluir agência",
      );

      // Invalida o cache e força um novo fetch
      await queryClient.invalidateQueries({ queryKey: ["agencias"] });
      
      toast({
        title: "Sucesso",
        description: "Agência excluída com sucesso",
      });
    } catch (error) {
      console.error("Erro completo:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir a agência",
        variant: "destructive",
      });
    }
  };

  // Filtragem e ordenação de agências
  const filteredAgencias = useMemo(() => {
    let resultado = agencias.filter((agencia) =>
      agencia.nome.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filtrar por porte
    if (porteFiltro !== "todas") {
      resultado = resultado.filter((agencia) => agencia.categorias?.includes(porteFiltro as AgenciaPorte));
    }

    // Ordenar: favoritas sempre no topo, depois a ordenação escolhida.
    resultado.sort((a, b) => {
      if (!!a.favorita !== !!b.favorita) return a.favorita ? -1 : 1;
      switch (ordenacao) {
        case "nome_asc":
          return a.nome.localeCompare(b.nome);
        case "nome_desc":
          return b.nome.localeCompare(a.nome);
        case "porte_asc":
          return (a.categorias?.[0] || "Z").localeCompare(b.categorias?.[0] || "Z");
        case "porte_desc":
          return (b.categorias?.[0] || "").localeCompare(a.categorias?.[0] || "");
        default:
          return 0;
      }
    });

    return resultado;
  }, [agencias, searchTerm, porteFiltro, ordenacao]);

  const [currentPage, setCurrentPage] = usePageParam();
  const PAGE_SIZE = 24;
  const totalPages = Math.max(1, Math.ceil(filteredAgencias.length / PAGE_SIZE));
  const paginaEfetiva = Math.min(currentPage, totalPages);
  const agenciasPagina = filteredAgencias.slice(
    (paginaEfetiva - 1) * PAGE_SIZE,
    paginaEfetiva * PAGE_SIZE,
  );

  const handleToggleFavorita = async (agencia: Agencia) => {
    try {
      await toggleFavorita.mutateAsync({
        id: agencia.id,
        favorita: !agencia.favorita,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível atualizar a favorita.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agências"
        subtitle="Gerencie as agências cadastradas no sistema"
        actions={
          <AgenciaModal onSuccess={() => queryClient.invalidateQueries({ queryKey: ["agencias"] })} />
        }
      />

      <FilterToolbar>
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Buscar agências..."
        />

        <Select
          value={porteFiltro}
          onValueChange={(value) => setPorteFiltro(value as AgenciaPorte | "todas")}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Porte" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">
              <span className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Todos os portes
              </span>
            </SelectItem>
            {AGENCIA_PORTES.map((porte) => (
              <SelectItem key={porte} value={porte}>
                Porte {porte}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={ordenacao}
          onValueChange={(value) => setOrdenacao(value as OrdenacaoType)}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Ordenar por" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nome_asc">
              <span className="flex items-center gap-2">
                <ArrowDownAZ className="h-4 w-4" />
                Nome A-Z
              </span>
            </SelectItem>
            <SelectItem value="nome_desc">
              <span className="flex items-center gap-2">
                <ArrowUpAZ className="h-4 w-4" />
                Nome Z-A
              </span>
            </SelectItem>
            <SelectItem value="porte_asc">
              <span className="flex items-center gap-2">
                <ArrowDownAZ className="h-4 w-4" />
                Porte A-E
              </span>
            </SelectItem>
            <SelectItem value="porte_desc">
              <span className="flex items-center gap-2">
                <ArrowUpAZ className="h-4 w-4" />
                Porte E-A
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </FilterToolbar>

      {isLoading ? (
        <LoadingState variant="grid" />
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : filteredAgencias.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Nenhuma agência encontrada"
          description={
            searchTerm || porteFiltro !== "todas"
              ? "Ajuste a busca ou os filtros para ver mais resultados."
              : "Cadastre a primeira agência para começar."
          }
        />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {agenciasPagina.map((agencia) => (
            <Card
              key={agencia.id}
              className="cursor-pointer transition-shadow hover:shadow-md"
              {...linkProps(`/agencias/${agencia.id}`)}
            >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-2">
                <div onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => handleToggleFavorita(agencia)}
                    disabled={toggleFavorita.isPending}
                    title={agencia.favorita ? "Remover dos favoritos" : "Marcar como favorita"}
                  >
                    <Star
                      className={cn(
                        "h-4 w-4",
                        agencia.favorita
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-muted-foreground",
                      )}
                    />
                  </Button>
                </div>
                <CardTitle className="text-xl font-bold">{agencia.nome}</CardTitle>
                {agencia.categorias && agencia.categorias.length > 0 && (
                  <div className="flex gap-1">
                    {agencia.categorias.map((cat) => (
                      <Badge key={cat} variant="outline" className="font-bold">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {agencia.segmento && (
                <CardDescription>{agencia.segmento}</CardDescription>
              )}
              <div className="space-y-2 text-sm">
                {agencia.email && (
                  <div>
                    <span className="font-medium">Email:</span> {agencia.email}
                  </div>
                )}
                {agencia.telefone && (
                  <div>
                    <span className="font-medium">Telefone:</span>{" "}
                    {agencia.telefone}
                  </div>
                )}
                {agencia.cidade && (
                  <div>
                    <span className="font-medium">Cidade:</span> {agencia.cidade}
                    {agencia.bairro && ` - ${agencia.bairro}`}
                  </div>
                )}
              </div>
              <div
                className="flex items-center justify-end gap-2 pt-4"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedAgencia(agencia)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setAgenciaParaExcluir({ id: agencia.id, nome: agencia.nome })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <ListPagination
          currentPage={paginaEfetiva}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {selectedAgencia && (
        <AgenciaEditModal
          agencia={selectedAgencia}
          onClose={() => setSelectedAgencia(null)}
          onSuccess={() => {
            setSelectedAgencia(null);
            queryClient.invalidateQueries({ queryKey: ["agencias"] });
          }}
        />
      )}

      <AlertDialog open={!!agenciaParaExcluir} onOpenChange={(open) => !open && setAgenciaParaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {agenciaParaExcluir?.nome}?</AlertDialogTitle>
            <AlertDialogDescription>
              A exclusão é definitiva. As vagas e os usuários dela ficam sem agência e as indicações de
              freelancers para ela são apagadas. Se ela tiver propostas, a exclusão é bloqueada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Manter agência</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (agenciaParaExcluir) void handleDelete(agenciaParaExcluir.id);
                setAgenciaParaExcluir(null);
              }}
            >
              Excluir agência
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
